import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Calendar, Tag, Crown, LogOut, Trash2, Plus, ExternalLink, Check, BarChart3, Megaphone, Eye, MousePointerClick, MapPin } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RANGER_AVATAR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import ToursManager from "@/pages/owner/ToursManager";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";
const CATS = ["Places to Eat + Drink", "Galleries + Studios", "Specialty Shops + Salons", "Community + Business Services"];

const TIER_LABEL = { free: "Free", pro: "Pro", premium: "Premium" };

function ProfileTab() {
  const [b, setB] = useState(null);
  const [f, setF] = useState({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/owner/business").then((r) => {
      if (r.data) { setB(r.data); setF((prev) => (prev && prev.name ? prev : r.data)); }
      setLoaded(true);
    });
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/owner/business", f);
      setB(data); setF(data);
      toast.success("Listing saved — it's live now");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!loaded) return <div className="text-neutral-500">Loading...</div>;
  const fields = [
    ["name", "Business name", "text"], ["tagline", "Tagline", "text"],
    ["address", "Address", "text"], ["phone", "Phone", "text"],
    ["website", "Website", "text"], ["instagram", "Instagram", "text"],
    ["hours", "Hours", "text"], ["image", "Main image URL", "text"],
  ];
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-1">Your Listing</h2>
      <p className="text-neutral-500 text-sm mb-6">Changes publish to the directory immediately. {b?.slug && <a href={`/directory/${b.slug}`} target="_blank" rel="noreferrer" className="text-[#00E5FF] inline-flex items-center gap-1">View live <ExternalLink size={12} /></a>}</p>
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <label className="text-neutral-400 text-xs">Category</label>
          <select value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="owner-biz-category" className={`mt-1 ${inputCls}`}>
            <option value="">Select...</option>
            {CATS.map((c) => <option key={c} value={c} className="bg-[#141414]">{c}</option>)}
          </select>
        </div>
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="text-neutral-400 text-xs">{label}</label>
            <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`owner-biz-${k}`} className={`mt-1 ${inputCls}`} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className="text-neutral-400 text-xs">Description</label>
          <textarea rows={4} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="owner-biz-description" className={`mt-1 ${inputCls}`} />
        </div>
        <Button type="submit" data-testid="owner-biz-save" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none w-fit">Save Listing</Button>
      </form>
    </div>
  );
}

function ListTab({ kind, title, fields }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({});
  const load = () => api.get(`/owner/${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [kind]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/owner/${kind}`, f); toast.success("Added & live"); setF({}); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/owner/${kind}/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const titleKey = kind === "events" ? "title" : "title";
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-5">{title} <span className="text-neutral-500 text-base">({items.length})</span></h2>
      <form onSubmit={add} className="border border-white/10 bg-[#141414] p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 max-w-3xl">
        {fields.map(([k, label, type]) => (
          <div key={k} className={type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="text-neutral-400 text-xs">{label}</label>
            {type === "textarea"
              ? <textarea rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`owner-${kind}-${k}`} className={`mt-1 ${inputCls}`} />
              : <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`owner-${kind}-${k}`} className={`mt-1 ${inputCls}`} />}
          </div>
        ))}
        <Button type="submit" data-testid={`owner-${kind}-add`} className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none w-fit sm:col-span-2"><Plus size={15} /> Add</Button>
      </form>
      <div className="space-y-2 max-w-3xl">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between border border-white/10 bg-[#141414] p-3" data-testid={`owner-${kind}-row-${it.id}`}>
            <div className="min-w-0">
              <div className="text-white text-sm font-medium truncate">{it[titleKey]}</div>
              <div className="text-neutral-500 text-xs truncate">{it.date || it.discount || ""} {it.venue || ""}</div>
            </div>
            <button onClick={() => del(it.id)} data-testid={`owner-${kind}-delete-${it.id}`} className="text-neutral-500 hover:text-[#FF0055]"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-neutral-500 text-sm py-6">Nothing yet — add your first {kind === "events" ? "event" : "offer"}.</div>}
      </div>
    </div>
  );
}

const PLANS = [
  { key: "pro_monthly", tier: "pro", name: "Pro", price: 29, color: "#FF0055", features: ["Photo gallery", "Unlimited events & offers", "Join promotions", "Monthly performance email"] },
  { key: "premium_monthly", tier: "premium", name: "Premium", price: 99, color: "#FFEA00", features: ["Everything in Pro", "Featured & homepage placement", "Advertising slots", "Priority Ranger coverage"] },
];

function MembershipTab() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/owner/business").then((r) => setB(r.data)); }, []);
  const current = b?.tier || "free";
  const upgrade = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(""); }
  };
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-1">Membership</h2>
      <p className="text-neutral-500 text-sm mb-6">Current plan: <span className="text-white font-semibold" data-testid="current-tier">{TIER_LABEL[current]}</span>. Upgrade to unlock more reach. Test card: 4242 4242 4242 4242.</p>
      {!b && <div className="text-[#FFEA00] text-sm mb-4">Create your listing first (Profile tab), then choose a plan.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
        {PLANS.map((p) => {
          const active = current === p.tier;
          return (
            <div key={p.key} className="border bg-[#141414] p-6" style={{ borderColor: active ? p.color : "rgba(255,255,255,0.1)" }} data-testid={`plan-${p.tier}`}>
              <span className="font-accent text-sm" style={{ color: p.color }}>{p.name}</span>
              <div className="mt-2 flex items-end gap-1"><span className="font-display font-black text-4xl text-white">${p.price}</span><span className="text-neutral-500 text-sm mb-1">/mo</span></div>
              <ul className="mt-4 space-y-2">
                {p.features.map((ft) => <li key={ft} className="flex items-start gap-2 text-neutral-300 text-sm"><Check size={14} className="mt-0.5" style={{ color: p.color }} /> {ft}</li>)}
              </ul>
              <Button disabled={active || busy === p.key || !b} onClick={() => upgrade(p.key)} data-testid={`upgrade-${p.tier}`}
                className="mt-6 w-full rounded-none font-semibold disabled:opacity-50" style={{ background: active ? "#333" : p.color, color: p.tier === "premium" ? "#000" : "#fff" }}>
                {active ? "Current Plan" : busy === p.key ? "Redirecting..." : `Upgrade to ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InsightsTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/insights").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-neutral-500">Loading...</div>;
  const Row = ({ label, views, clicks }) => (
    <div className="flex items-center justify-between border-b border-white/5 py-3 text-sm">
      <span className="text-white truncate pr-4">{label}</span>
      <div className="flex gap-6 flex-shrink-0">
        <span className="text-neutral-400 flex items-center gap-1"><Eye size={13} className="text-[#00E5FF]" /> {views}</span>
        <span className="text-neutral-400 flex items-center gap-1"><MousePointerClick size={13} className="text-[#FF0055]" /> {clicks}</span>
      </div>
    </div>
  );
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-1">Insights</h2>
      <p className="text-neutral-500 text-sm mb-6">How many people are seeing and engaging with your listing, events, and offers.</p>
      <div className="grid grid-cols-2 gap-4 max-w-md mb-8">
        <div className="border border-white/10 bg-[#141414] p-5"><Eye className="text-[#00E5FF]" size={20} /><div className="font-display font-extrabold text-3xl text-white mt-2" data-testid="insights-total-views">{d.totals.views}</div><div className="text-neutral-400 text-xs">Total Views</div></div>
        <div className="border border-white/10 bg-[#141414] p-5"><MousePointerClick className="text-[#FF0055]" size={20} /><div className="font-display font-extrabold text-3xl text-white mt-2" data-testid="insights-total-clicks">{d.totals.clicks}</div><div className="text-neutral-400 text-xs">Total Clicks</div></div>
      </div>
      <div className="max-w-2xl space-y-6">
        <div>
          <h3 className="font-accent text-[#00E5FF] text-sm mb-2">Listing</h3>
          <Row label={d.business.name || "Create your listing first"} views={d.business.views} clicks={d.business.clicks} />
        </div>
        <div>
          <h3 className="font-accent text-[#00E5FF] text-sm mb-2">Events</h3>
          {d.events.length ? d.events.map((e) => <Row key={e.id} label={e.title} views={e.views} clicks={e.clicks} />) : <div className="text-neutral-500 text-sm">No events yet.</div>}
        </div>
        <div>
          <h3 className="font-accent text-[#00E5FF] text-sm mb-2">Offers</h3>
          {d.offers.length ? d.offers.map((o) => <Row key={o.id} label={o.title} views={o.views} clicks={o.clicks} />) : <div className="text-neutral-500 text-sm">No offers yet.</div>}
        </div>
      </div>
    </div>
  );
}

const AD_STATUS_COLOR = { active: "#00FF66", pending: "#FFEA00", rejected: "#FF0055", expired: "#888" };

function AdsTab() {
  const [ads, setAds] = useState([]);
  useEffect(() => { api.get("/owner/ads").then((r) => setAds(r.data)); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-2xl text-white">Your Ads <span className="text-neutral-500 text-base">({ads.length})</span></h2>
        <Link to="/advertise"><Button data-testid="owner-new-ad" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none"><Megaphone size={15} /> New Ad</Button></Link>
      </div>
      <div className="border border-[#FFEA00]/30 bg-[#FFEA00]/5 p-4 text-sm text-[#FFEA00] mb-6">Premium members get 20% off all ad placements at checkout.</div>
      <div className="space-y-2 max-w-3xl">
        {ads.map((a) => (
          <div key={a.id} className="flex items-center justify-between border border-white/10 bg-[#141414] p-4" data-testid={`owner-ad-${a.id}`}>
            <div className="min-w-0">
              <div className="text-white text-sm font-medium">{a.label}</div>
              <div className="text-neutral-500 text-xs truncate">{a.headline || a.advertiser_name} · {a.quantity} {a.unit}</div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="text-white font-semibold text-sm">${a.total}</span>
              <span className="text-xs font-accent px-2 py-1" style={{ color: AD_STATUS_COLOR[a.status], border: `1px solid ${AD_STATUS_COLOR[a.status]}55` }}>{a.status}</span>
            </div>
          </div>
        ))}
        {ads.length === 0 && <div className="text-neutral-500 text-sm py-6">No ads yet. Create your first placement to get seen across RiNo.</div>}
      </div>
    </div>
  );
}

const TABS = [
  { id: "profile", label: "Listing", icon: Store },
  { id: "events", label: "Events", icon: Calendar },
  { id: "offers", label: "Offers", icon: Tag },
  { id: "tours", label: "Tours", icon: MapPin },
  { id: "insights", label: "Insights", icon: BarChart3 },
  { id: "advertising", label: "Advertising", icon: Megaphone },
  { id: "membership", label: "Membership", icon: Crown },
];

export default function OwnerDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  useEffect(() => { if (!loading && !user) nav("/owner/login"); }, [user, loading, nav]);
  if (loading || !user) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Seo title="Business Dashboard" description="Manage your Visit RiNo listing, events, offers, and membership." />
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={RANGER_AVATAR} alt="" className="w-8 h-8 object-cover border border-[#FF0055]" />
            <span className="font-display font-extrabold text-white">VISIT<span className="text-[#FF0055]">RiNo</span> <span className="text-neutral-500 text-sm font-normal">Business</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-neutral-400 text-sm hidden sm:block">{user.name || user.email}</span>
            <button onClick={() => { logout(); nav("/"); }} data-testid="owner-logout" className="text-neutral-400 hover:text-[#FF0055] flex items-center gap-1 text-sm"><LogOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <nav className="lg:col-span-3 flex lg:flex-col gap-1 flex-wrap">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`owner-tab-${t.id}`}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${tab === t.id ? "text-white bg-[#FF0055]/10 border-l-2 border-[#FF0055]" : "text-neutral-400 hover:text-white border-l-2 border-transparent"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <main className="lg:col-span-9">
          {tab === "profile" && <ProfileTab />}
          {tab === "events" && <ListTab kind="events" title="Your Events" fields={[["title", "Title", "text"], ["date", "Date (YYYY-MM-DD)", "text"], ["time", "Time", "text"], ["venue", "Venue", "text"], ["address", "Address", "text"], ["category", "Category", "text"], ["price", "Price", "text"], ["ticket_url", "Ticket link (URL)", "text"], ["image", "Image URL", "text"], ["description", "Description", "textarea"]]} />}
          {tab === "offers" && <ListTab kind="offers" title="Your Offers" fields={[["title", "Title", "text"], ["discount", "Discount (e.g. 15% off)", "text"], ["valid_until", "Valid until (text)", "text"], ["ends_at", "End date (YYYY-MM-DD for countdown)", "text"], ["category", "Category", "text"], ["image", "Image URL", "text"], ["description", "Description", "textarea"]]} />}
          {tab === "tours" && <ToursManager />}
          {tab === "membership" && <MembershipTab />}
          {tab === "insights" && <InsightsTab />}
          {tab === "advertising" && <AdsTab />}
        </main>
      </div>
    </div>
  );
}
